import { useState } from 'react';
import {
  Paper,
  TextInput,
  PasswordInput,
  Button,
  Title,
  Text,
  Container,
  Stack,
  Alert,
  Center,
  Box,
  Anchor,
  Modal,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { useAuthStore } from '../../stores/auth';
import { authService } from '../../services/api/auth';
import { SafeLoadIcon } from '../../components/shared';

export function LoginPage() {
  const { login, isLoading, error, clearError } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Forgot password modal state
  const [forgotOpened, { open: openForgot, close: closeForgot }] = useDisclosure(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSubmitted, setForgotSubmitted] = useState(false);
  const [forgotError, setForgotError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    await login({ email, password });
  };

  const handleForgotSubmit = async () => {
    if (!forgotEmail.trim()) {
      setForgotError('Please enter your email address.');
      return;
    }

    setForgotLoading(true);
    setForgotError('');
    try {
      await authService.forgotPassword(forgotEmail.trim());
      setForgotSubmitted(true);
    } catch (err) {
      setForgotError(err instanceof Error ? err.message : 'An error occurred. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleForgotClose = () => {
    closeForgot();
    setForgotEmail('');
    setForgotError('');
    setForgotSubmitted(false);
  };

  return (
    <Box
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#f8f9fa',
      }}
    >
      <Container size={420} style={{ width: '100%' }}>
        <Center mb="xl">
          <Stack align="center" gap="xs">
            <SafeLoadIcon size={60} />
            <Title order={1} size="h2" ta="center" c="dark">
              SafeLoad Admin
            </Title>
            <Text c="dimmed" size="sm" ta="center">
              Sign in to manage licenses
            </Text>
          </Stack>
        </Center>

        <Paper withBorder shadow="md" p={30} radius="md">
          <form onSubmit={handleSubmit}>
            <Stack gap="md">
              {error && (
                <Alert icon={<IconAlertCircle size={16} />} color="red" title="Error">
                  {error}
                </Alert>
              )}

              <TextInput
                label="Email"
                placeholder="you@leica.com"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                autoComplete="email"
              />

              <PasswordInput
                label="Password"
                placeholder="Your password"
                required
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete="current-password"
              />

              <Button type="submit" fullWidth loading={isLoading}>
                Sign in
              </Button>

              <Center>
                <Anchor component="button" type="button" size="sm" onClick={openForgot}>
                  Forgot Password?
                </Anchor>
              </Center>
            </Stack>
          </form>
        </Paper>
      </Container>

      {/* Forgot Password Modal */}
      <Modal
        opened={forgotOpened}
        onClose={handleForgotClose}
        title="Forgot Password"
        size="md"
      >
        {forgotSubmitted ? (
          <Stack gap="md">
            <Alert icon={<IconCheck size={16} />} color="green" title="Email Sent">
              If an account with that email exists, you will receive an email with instructions to
              reset your password.
            </Alert>
            <Button variant="light" fullWidth onClick={handleForgotClose}>
              Return to Login
            </Button>
          </Stack>
        ) : (
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              Enter your email to reset your password for the SafeLoad License Management portal.
              If qualified, you will receive an email with instructions to reset your password and
              you will be able to log in.
            </Text>

            {forgotError && (
              <Alert icon={<IconAlertCircle size={16} />} color="red">
                {forgotError}
              </Alert>
            )}

            <TextInput
              label="Email"
              placeholder="you@leica.com"
              required
              value={forgotEmail}
              onChange={e => setForgotEmail(e.target.value)}
              autoComplete="email"
            />

            <Stack gap="xs" mt="md">
              <Button fullWidth loading={forgotLoading} onClick={handleForgotSubmit}>
                Submit
              </Button>
              <Button variant="light" fullWidth onClick={handleForgotClose}>
                Cancel
              </Button>
            </Stack>
          </Stack>
        )}
      </Modal>
    </Box>
  );
}
